import {
  UploadOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  SaveOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import {
  Button,
  Table,
  message,
  notification,
  Alert,
  Upload,
  Select,
  Input,
  Tag,
  Space,
  Card,
  DatePicker,
  Progress,
} from "antd";
import dayjs from "dayjs";
import type { ColumnsType } from "antd/lib/table";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from 'xlsx';
import { CURRENT_CLUB_ID } from "../../_helpers";
import {
  usersApi,
  ranksApi,
  teamsApi,
  userClubsApi,
  ArcherUpsertRequest,
} from "../../_helpers/apiService";
import { PageTitle } from "../../components";

const { Option } = Select;
const { Dragger } = Upload;

// ============================================================================
// Types
// ============================================================================

interface ValidationError {
  field: string;
  message: string;
}

interface ImportedArcher {
  key: string;
  // Excel data
  rank_name?: string;
  rank_year?: string;
  provincial_rank_name?: string;
  provincial_rank_year?: string;
  rate_name?: string;
  rate_year?: string;
  team_name?: string;
  affiliation?: string;
  last_name?: string;
  first_name?: string;
  ssn?: string;
  phone_number?: string;
  gender?: string;
  email?: string;
  // Matched IDs
  rank_id?: number;
  provincial_rank_id?: number;
  rate_id?: number;
  team_id?: number;
  rank_date?: string;
  provincial_rank_date?: string;
  rate_date?: string;
  // Suggestions for unmatched
  rank_suggestions?: any[];
  provincial_rank_suggestions?: any[];
  rate_suggestions?: any[];
  team_suggestions?: any[];
  // Existing user info
  existing_user?: {
    id: number;
    email?: string;
    first_name: string;
    last_name: string;
    phone_number?: string;
    is_member: boolean;
  } | null;
  // Validation
  is_valid: boolean;
  validation_errors: ValidationError[];
  // Import status
  import_status?: 'pending' | 'success' | 'error';
  import_error?: string;
}

interface RankMap {
  [key: number]: {
    id: number;
    name: string;
    is_rank: boolean;
    order: number;
  };
}

interface TeamMap {
  [key: number]: {
    id: number;
    name: string;
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

// Normalize SSN (convert Cyrillic to Latin and uppercase)
const normalizeSSN = (ssn: string): string => {
  const cyrillicToLatin: Record<string, string> = {
    'А': 'A', 'В': 'B', 'Е': 'E', 'К': 'K', 'М': 'M', 'Н': 'H', 'О': 'O',
    'Р': 'P', 'С': 'C', 'Т': 'T', 'У': 'Y', 'Х': 'X',
    'а': 'A', 'в': 'B', 'е': 'E', 'к': 'K', 'м': 'M', 'н': 'H', 'о': 'O',
    'р': 'P', 'с': 'C', 'т': 'T', 'у': 'Y', 'х': 'X',
  };

  return ssn.split('').map(char => cyrillicToLatin[char] || char).join('').toUpperCase();
};

// Convert year string to date
const yearToDate = (yearStr: string | undefined): string => {
  if (!yearStr) {
    return `${new Date().getFullYear()}-01-01`;
  }
  const yearMatch = yearStr.match(/\d+/);
  if (yearMatch) {
    const year = parseInt(yearMatch[0], 10);
    const fullYear = year < 100 ? (year > 50 ? 1900 + year : 2000 + year) : year;
    return `${fullYear}-01-01`;
  }
  return `${new Date().getFullYear()}-01-01`;
};

// Find matching rank/rate
const findMatchingRank = (
  name: string | undefined,
  ranks: RankMap,
  isRate: boolean
): { id: number | undefined; suggestions: any[] } => {
  if (!name) return { id: undefined, suggestions: [] };

  const ranksList = Object.values(ranks).filter((r) =>
    isRate ? r.is_rank : !r.is_rank
  );

  const exactMatch = ranksList.find((r) =>
    r.name.toLowerCase() === name.toLowerCase()
  );

  if (exactMatch) {
    return { id: exactMatch.id, suggestions: [] };
  }

  const suggestions = ranksList.filter((r) => {
    const rankName = r.name.toLowerCase();
    const searchName = name.toLowerCase();
    return rankName.includes(searchName) || searchName.includes(rankName);
  });

  return { id: undefined, suggestions };
};

// Find matching team
const findMatchingTeam = (
  name: string | undefined,
  teams: TeamMap
): { id: number | undefined; suggestions: any[] } => {
  if (!name) return { id: undefined, suggestions: [] };

  const teamsList = Object.values(teams);

  const exactMatch = teamsList.find((t) =>
    t.name.toLowerCase() === name.toLowerCase()
  );

  if (exactMatch) {
    return { id: exactMatch.id, suggestions: [] };
  }

  const suggestions = teamsList.filter((t) => {
    const teamName = t.name.toLowerCase();
    const searchName = name.toLowerCase();
    return teamName.includes(searchName) || searchName.includes(teamName);
  });

  return { id: undefined, suggestions };
};

// Validate a single row
const validateRow = (row: ImportedArcher): ValidationError[] => {
  const errors: ValidationError[] = [];

  if (!row.first_name?.trim()) {
    errors.push({ field: 'first_name', message: 'Нэр оруулна уу' });
  }

  if (!row.last_name?.trim()) {
    errors.push({ field: 'last_name', message: 'Овог оруулна уу' });
  }

  if (!row.ssn?.trim()) {
    errors.push({ field: 'ssn', message: 'РД оруулна уу' });
  } else if (row.ssn.trim().length < 10) {
    errors.push({ field: 'ssn', message: 'РД дор хаяж 10 тэмдэгт байх ёстой' });
  }

  if (!row.phone_number?.trim()) {
    errors.push({ field: 'phone_number', message: 'Утас оруулна уу' });
  } else if (!/^\d{8,}$/.test(row.phone_number.replace(/\D/g, ''))) {
    errors.push({ field: 'phone_number', message: 'Утасны дугаар буруу байна' });
  }

  if (!row.team_id) {
    errors.push({ field: 'team_id', message: 'Баг сонгоно уу' });
  }

  if (row.email?.trim()) {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(row.email.trim())) {
      errors.push({ field: 'email', message: 'Имэйл хаяг буруу байна' });
    }
  }

  return errors;
};

// ============================================================================
// Component
// ============================================================================

function ArcherImport() {
  const navigate = useNavigate();
  const [importedData, setImportedData] = useState<ImportedArcher[]>([]);
  const [fileLoading, setFileLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [ranks, setRanks] = useState<RankMap>({});
  const [teams, setTeams] = useState<TeamMap>({});
  const [importProgress, setImportProgress] = useState<{
    current: number;
    total: number;
    successCount: number;
    errorCount: number;
  } | null>(null);

  // Fetch ranks and teams on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ranksRes, teamsRes] = await Promise.all([
          ranksApi.getAll({ club_id: CURRENT_CLUB_ID, limit: 1000 }),
          teamsApi.getAll({ club_id: CURRENT_CLUB_ID, limit: 1000 }),
        ]);

        const ranksObj: RankMap = {};
        ranksRes.data.items.forEach((rank: any) => {
          ranksObj[rank.id] = rank;
        });
        setRanks(ranksObj);

        const teamsObj: TeamMap = {};
        teamsRes.data.items.forEach((team: any) => {
          teamsObj[team.id] = team;
        });
        setTeams(teamsObj);
      } catch (error) {
        console.error("Fetch data error:", error);
        message.error("Өгөгдөл татахад алдаа гарлаа");
      }
    };

    fetchData();
  }, []);

  // Update row and revalidate
  const updateRow = useCallback((key: string, field: string, value: any) => {
    setImportedData((prev) =>
      prev.map((row) => {
        if (row.key !== key) return row;

        let normalizedValue = value;

        // Normalize SSN
        if (field === 'ssn' && typeof value === 'string') {
          normalizedValue = normalizeSSN(value);
        }

        // Clean email
        if (field === 'email' && typeof value === 'string') {
          normalizedValue = value.replace(/[\u200B-\u200D\uFEFF]/g, '').replace(/\s+/g, '').trim();
        }

        const updatedRow = { ...row, [field]: normalizedValue };
        const errors = validateRow(updatedRow);

        return {
          ...updatedRow,
          is_valid: errors.length === 0,
          validation_errors: errors,
          // Clear import error when user edits the field
          import_status: row.import_status === 'error' ? 'pending' : row.import_status,
          import_error: row.import_status === 'error' ? undefined : row.import_error,
        };
      })
    );
  }, []);

  // Delete row
  const deleteRow = useCallback((key: string) => {
    setImportedData((prev) => prev.filter((row) => row.key !== key));
    message.success('Мөр устгагдлаа');
  }, []);

  // Parse Excel file
  const handleExcelUpload = async (file: File) => {
    setFileLoading(true);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const rawData: any[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      // Skip header row and filter empty rows
      const dataRows = rawData.slice(1).filter((row) => row && row.length > 0);

      // Process each row
      const processedData: ImportedArcher[] = await Promise.all(
        dataRows.map(async (row: any[], index: number) => {
          // Column indices based on template:
          // 0: №, 1: ЦОЛ, 2: СПОРТЫН ЦОЛ БИЕЛҮҮЛСЭН ОН, 3: УЛС АЙМГИЙН ЦОЛ БИЕЛҮҮЛСЭН ОН
          // 4: БАГ КЛУБ, 5: ХАРЪЯАЛАЛ, 6: Овог, 7: Нэр, 8: Регистрийн дугаар
          // 9: Утас, 10: Хүйс, 11: Имэйл

          const rawRankField = String(row[1] || '').trim();
          const rankParts = rawRankField.split(',').map((s) => s.trim());
          const rankName = rankParts[0] || '';
          const rateName = rankParts.length > 1 ? rankParts.slice(1).join(', ') : '';

          const ssn = normalizeSSN(String(row[8] || '').trim());
          const teamName = String(row[4] || '').trim();
          const phoneRaw = String(row[9] || '').trim();
          const genderRaw = String(row[10] || '').trim().toLowerCase();

          // Find matching rank, rate, and team
          const rankMatch = findMatchingRank(rankName, ranks, false);
          const rateMatch = findMatchingRank(rateName, ranks, true);
          const teamMatch = findMatchingTeam(teamName, teams);

          // Check if user exists by SSN
          let existingUser = null;
          if (ssn) {
            try {
              const res = await usersApi.getBySSN(ssn, CURRENT_CLUB_ID);
              if (res.data.found && res.data.user) {
                existingUser = {
                  id: res.data.user.id,
                  email: res.data.user.email,
                  first_name: res.data.user.first_name,
                  last_name: res.data.user.last_name,
                  phone_number: res.data.user.phone_number,
                  is_member: res.data.is_member,
                };
              }
            } catch (error) {
              console.error('Error fetching user by SSN:', error);
            }
          }

          // Normalize gender
          let gender = 'male';
          if (genderRaw.includes('эм') || genderRaw === 'ж' || genderRaw === 'f') {
            gender = 'female';
          }

          // Clean phone number
          const phoneNumber = phoneRaw.replace(/[^0-9]/g, '');

          // Clean email
          let email = String(row[11] || '').trim();
          email = email.replace(/[\u200B-\u200D\uFEFF]/g, '').replace(/\s+/g, '').trim();
          // Use existing user's email if Excel cell is empty
          if (!email && existingUser?.email) {
            email = existingUser.email;
          }

          // Build the imported archer object
          const archer: ImportedArcher = {
            key: `import-${index}`,
            rank_name: rankName,
            rank_year: String(row[3] || ''),
            rate_name: rateName,
            rate_year: String(row[2] || ''),
            team_name: teamName,
            affiliation: String(row[5] || '').trim(),
            last_name: existingUser?.last_name || String(row[6] || '').trim(),
            first_name: existingUser?.first_name || String(row[7] || '').trim(),
            ssn: ssn,
            phone_number: existingUser?.phone_number?.replace('+976', '') || phoneNumber,
            gender: gender,
            email: email,
            rank_id: rankMatch.id,
            rank_suggestions: rankMatch.suggestions,
            rate_id: rateMatch.id,
            rate_suggestions: rateMatch.suggestions,
            team_id: teamMatch.id,
            team_suggestions: teamMatch.suggestions,
            rank_date: rankMatch.id ? yearToDate(String(row[3] || '')) : undefined,
            rate_date: rateMatch.id ? yearToDate(String(row[2] || '')) : undefined,
            existing_user: existingUser,
            is_valid: true,
            validation_errors: [],
            import_status: 'pending',
          };

          // Validate
          const errors = validateRow(archer);
          archer.is_valid = errors.length === 0;
          archer.validation_errors = errors;

          return archer;
        })
      );

      setImportedData(processedData);

      const invalidCount = processedData.filter((row) => !row.is_valid).length;
      if (invalidCount > 0) {
        message.warning(`${processedData.length} мөр уншигдлаа. ${invalidCount} мөрт алдаа байна.`);
      } else {
        message.success(`${processedData.length} мөр амжилттай уншигдлаа`);
      }
    } catch (error) {
      console.error('Excel parse error:', error);
      message.error('Excel файл унших үед алдаа гарлаа');
    } finally {
      setFileLoading(false);
    }

    return false; // Prevent default upload behavior
  };

  // Save all archers
  const handleSave = async () => {
    // Check validation
    const invalidRows = importedData.filter((row) => !row.is_valid);
    if (invalidRows.length > 0) {
      message.error(`${invalidRows.length} мөрт алдаа байна. Эхлээд алдаануудыг засна уу.`);
      return;
    }

    setSaveLoading(true);
    setImportProgress({
      current: 0,
      total: importedData.length,
      successCount: 0,
      errorCount: 0,
    });

    let successCount = 0;
    let errorCount = 0;

    // Process each archer one by one
    for (let i = 0; i < importedData.length; i++) {
      const row = importedData[i];

      try {
        const upsertData: ArcherUpsertRequest = {
          ssn: row.ssn!,
          first_name: row.first_name!,
          last_name: row.last_name!,
          phone_number: row.phone_number!,
          email: row.email || undefined,
          gender: row.gender || 'male',
          affiliation: row.affiliation || undefined,
          club_id: CURRENT_CLUB_ID,
          team_id: row.team_id!,
          rank_id: row.rank_id || undefined,
          rank_date: row.rank_date || undefined,
          rate_id: row.rate_id || undefined,
          rate_date: row.rate_date || undefined,
          is_team_leader: false,
        };

        await userClubsApi.upsertArcher(upsertData);

        // Mark as success
        setImportedData((prev) =>
          prev.map((r) =>
            r.key === row.key
              ? { ...r, import_status: 'success' as const }
              : r
          )
        );

        successCount++;
      } catch (error: any) {
        console.error('Error importing row:', row, error);

        let errorMessage = 'Тодорхойгүй алдаа';
        if (error.response?.data?.detail) {
          errorMessage = typeof error.response.data.detail === 'string'
            ? error.response.data.detail
            : JSON.stringify(error.response.data.detail);
        }

        // Mark as error
        setImportedData((prev) =>
          prev.map((r) =>
            r.key === row.key
              ? { ...r, import_status: 'error' as const, import_error: errorMessage }
              : r
          )
        );

        errorCount++;
      }

      // Update progress
      setImportProgress({
        current: i + 1,
        total: importedData.length,
        successCount,
        errorCount,
      });
    }

    setSaveLoading(false);

    if (successCount > 0 && errorCount === 0) {
      notification.success({
        message: 'Импорт дууслаа',
        description: `${successCount} харваач амжилттай бүртгэгдлээ.`,
        duration: 5,
      });
      // Navigate after a short delay
      setTimeout(() => navigate('/archers'), 1500);
    } else if (successCount > 0 && errorCount > 0) {
      notification.warning({
        message: 'Импорт хэсэгчлэн дууслаа',
        description: `${successCount} амжилттай, ${errorCount} алдаатай.`,
        duration: 5,
      });
    } else if (errorCount > 0) {
      notification.error({
        message: 'Импорт амжилтгүй',
        description: `${errorCount} алдаа гарлаа. Доорх мэдээллийг шалгана уу.`,
        duration: 5,
      });
    }

    setImportProgress(null);
  };

  // Get field error message
  const getFieldError = (record: ImportedArcher, field: string): string | undefined => {
    return record.validation_errors?.find((e) => e.field === field)?.message;
  };

  // Table columns
  const columns: ColumnsType<ImportedArcher> = [
    {
      title: '№',
      width: 60,
      fixed: 'left',
      render: (_, record, index) => {
        const hasValidationError = !record.is_valid;
        const hasImportError = record.import_status === 'error';
        const isSuccess = record.import_status === 'success';

        return (
          <Space size={4}>
            {isSuccess && <CheckCircleOutlined style={{ color: '#52c41a' }} />}
            {hasImportError && <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
            {hasValidationError && !hasImportError && <span style={{ color: '#ff4d4f' }}>!</span>}
            <span>{index + 1}</span>
          </Space>
        );
      },
    },
    {
      title: 'Нэр',
      dataIndex: 'first_name',
      width: 150,
      fixed: 'left',
      render: (value, record) => {
        const error = getFieldError(record, 'first_name');
        return (
          <div>
            <Input
              value={value}
              onChange={(e) => updateRow(record.key, 'first_name', e.target.value)}
              size="small"
              status={error ? 'error' : undefined}
              disabled={record.import_status === 'success'}
            />
            {error && <div style={{ color: '#ff4d4f', fontSize: 11 }}>{error}</div>}
          </div>
        );
      },
    },
    {
      title: 'Овог',
      dataIndex: 'last_name',
      width: 150,
      render: (value, record) => {
        const error = getFieldError(record, 'last_name');
        return (
          <div>
            <Input
              value={value}
              onChange={(e) => updateRow(record.key, 'last_name', e.target.value)}
              size="small"
              status={error ? 'error' : undefined}
              disabled={record.import_status === 'success'}
            />
            {error && <div style={{ color: '#ff4d4f', fontSize: 11 }}>{error}</div>}
          </div>
        );
      },
    },
    {
      title: 'РД',
      dataIndex: 'ssn',
      width: 130,
      render: (value, record) => {
        const error = getFieldError(record, 'ssn');
        return (
          <div>
            <Input
              value={value}
              onChange={(e) => updateRow(record.key, 'ssn', e.target.value)}
              size="small"
              status={error ? 'error' : undefined}
              disabled={record.import_status === 'success'}
            />
            {error && <div style={{ color: '#ff4d4f', fontSize: 11 }}>{error}</div>}
            {!error && record.existing_user && (
              <Tag color={record.existing_user.is_member ? 'green' : 'blue'} style={{ marginTop: 2 }}>
                {record.existing_user.is_member ? 'Гишүүн' : 'Системд бий'}
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: 'Утас',
      dataIndex: 'phone_number',
      width: 120,
      render: (value, record) => {
        const error = getFieldError(record, 'phone_number');
        return (
          <div>
            <Input
              value={value}
              onChange={(e) => updateRow(record.key, 'phone_number', e.target.value)}
              size="small"
              status={error ? 'error' : undefined}
              disabled={record.import_status === 'success'}
            />
            {error && <div style={{ color: '#ff4d4f', fontSize: 11 }}>{error}</div>}
          </div>
        );
      },
    },
    {
      title: 'Имэйл',
      dataIndex: 'email',
      width: 160,
      render: (value, record) => {
        const error = getFieldError(record, 'email');
        return (
          <div>
            <Input
              value={value}
              onChange={(e) => updateRow(record.key, 'email', e.target.value)}
              size="small"
              status={error ? 'error' : undefined}
              disabled={record.import_status === 'success'}
            />
            {error && <div style={{ color: '#ff4d4f', fontSize: 11 }}>{error}</div>}
          </div>
        );
      },
    },
    {
      title: 'Баг',
      dataIndex: 'team_id',
      width: 180,
      render: (value, record) => {
        const error = getFieldError(record, 'team_id');
        return (
          <div>
            <Select
              value={value}
              onChange={(val) => updateRow(record.key, 'team_id', val)}
              placeholder="Баг сонгох"
              size="small"
              showSearch
              allowClear
              style={{ width: '100%' }}
              status={error ? 'error' : undefined}
              disabled={record.import_status === 'success'}
              filterOption={(input, option) =>
                String(option?.children || '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {Object.values(teams)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((team) => (
                  <Option key={team.id} value={team.id}>{team.name}</Option>
                ))}
            </Select>
            {error && <div style={{ color: '#ff4d4f', fontSize: 11 }}>{error}</div>}
            {!error && !value && record.team_suggestions && record.team_suggestions.length > 0 && (
              <div style={{ marginTop: 2 }}>
                {record.team_suggestions.slice(0, 2).map((s) => (
                  <Tag
                    key={s.id}
                    color="orange"
                    style={{ cursor: 'pointer', fontSize: 11 }}
                    onClick={() => updateRow(record.key, 'team_id', s.id)}
                  >
                    {s.name}
                  </Tag>
                ))}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Улсын цол',
      dataIndex: 'rank_id',
      width: 160,
      render: (value, record) => (
        <div>
          <Select
            value={value}
            onChange={(val) => updateRow(record.key, 'rank_id', val)}
            placeholder="Цол"
            size="small"
            showSearch
            allowClear
            style={{ width: '100%' }}
            disabled={record.import_status === 'success'}
            filterOption={(input, option) =>
              String(option?.children || '').toLowerCase().includes(input.toLowerCase())
            }
          >
            {Object.values(ranks)
              .filter((r) => !r.is_rank)
              .sort((a, b) => a.order - b.order)
              .map((rank) => (
                <Option key={rank.id} value={rank.id}>{rank.name}</Option>
              ))}
          </Select>
          {!value && record.rank_suggestions && record.rank_suggestions.length > 0 && (
            <div style={{ marginTop: 2 }}>
              {record.rank_suggestions.slice(0, 2).map((s) => (
                <Tag
                  key={s.id}
                  color="orange"
                  style={{ cursor: 'pointer', fontSize: 11 }}
                  onClick={() => updateRow(record.key, 'rank_id', s.id)}
                >
                  {s.name}
                </Tag>
              ))}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Цол огноо',
      dataIndex: 'rank_date',
      width: 130,
      render: (value, record) => (
        <DatePicker
          value={value ? dayjs(value) : null}
          onChange={(date) => updateRow(record.key, 'rank_date', date?.format('YYYY-MM-DD') || null)}
          format="YYYY-MM-DD"
          size="small"
          style={{ width: '100%' }}
          disabled={record.import_status === 'success'}
        />
      ),
    },
    {
      title: 'Спортын зэрэг',
      dataIndex: 'rate_id',
      width: 160,
      render: (value, record) => (
        <div>
          <Select
            value={value}
            onChange={(val) => updateRow(record.key, 'rate_id', val)}
            placeholder="Зэрэг"
            size="small"
            showSearch
            allowClear
            style={{ width: '100%' }}
            disabled={record.import_status === 'success'}
            filterOption={(input, option) =>
              String(option?.children || '').toLowerCase().includes(input.toLowerCase())
            }
          >
            {Object.values(ranks)
              .filter((r) => r.is_rank)
              .sort((a, b) => a.order - b.order)
              .map((rank) => (
                <Option key={rank.id} value={rank.id}>{rank.name}</Option>
              ))}
          </Select>
          {!value && record.rate_suggestions && record.rate_suggestions.length > 0 && (
            <div style={{ marginTop: 2 }}>
              {record.rate_suggestions.slice(0, 2).map((s) => (
                <Tag
                  key={s.id}
                  color="orange"
                  style={{ cursor: 'pointer', fontSize: 11 }}
                  onClick={() => updateRow(record.key, 'rate_id', s.id)}
                >
                  {s.name}
                </Tag>
              ))}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Зэрэг огноо',
      dataIndex: 'rate_date',
      width: 130,
      render: (value, record) => (
        <DatePicker
          value={value ? dayjs(value) : null}
          onChange={(date) => updateRow(record.key, 'rate_date', date?.format('YYYY-MM-DD') || null)}
          format="YYYY-MM-DD"
          size="small"
          style={{ width: '100%' }}
          disabled={record.import_status === 'success'}
        />
      ),
    },
    {
      title: 'Харъяалал',
      dataIndex: 'affiliation',
      width: 150,
      render: (value, record) => (
        <Input
          value={value}
          onChange={(e) => updateRow(record.key, 'affiliation', e.target.value)}
          size="small"
          disabled={record.import_status === 'success'}
        />
      ),
    },
    {
      title: '',
      key: 'action',
      width: 50,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => deleteRow(record.key)}
          size="small"
          disabled={record.import_status === 'success'}
        />
      ),
    },
  ];

  // Stats
  const validCount = importedData.filter((r) => r.is_valid).length;
  const invalidCount = importedData.length - validCount;
  const successCount = importedData.filter((r) => r.import_status === 'success').length;
  const errorCount = importedData.filter((r) => r.import_status === 'error').length;
  const pendingCount = importedData.filter((r) => r.import_status === 'pending').length;

  return (
    <>
      <style>{`
        .import-row-success { background-color: #f6ffed !important; }
        .import-row-success:hover > td { background-color: #d9f7be !important; }
        .import-row-error { background-color: #fff1f0 !important; }
        .import-row-error:hover > td { background-color: #ffccc7 !important; }
      `}</style>

      <PageTitle
        title="Excel импорт"
        description="Олон харваачийг Excel файлаас импортлох"
        right={
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/archers')}
              size="large"
            >
              Буцах
            </Button>
            {importedData.length > 0 && (
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={saveLoading}
                disabled={invalidCount > 0 || pendingCount === 0}
                size="large"
              >
                Хадгалах ({validCount}/{importedData.length})
              </Button>
            )}
          </Space>
        }
      />

      <div className="page-content-wrapper">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <Card>
                {importedData.length === 0 ? (
                  <div style={{ padding: '40px 0' }}>
                    <Dragger
                      accept=".xlsx,.xls"
                      beforeUpload={handleExcelUpload}
                      showUploadList={false}
                      multiple={false}
                      disabled={fileLoading}
                    >
                      <p className="ant-upload-drag-icon">
                        <UploadOutlined style={{ fontSize: 48, color: '#1890ff' }} />
                      </p>
                      <p className="ant-upload-text" style={{ fontSize: 18 }}>
                        Excel файл энд чирж оруулах эсвэл дарж сонгоно уу
                      </p>
                      <p className="ant-upload-hint">
                        Зөвхөн .xlsx эсвэл .xls өргөтгөлтэй файл дэмжинэ
                      </p>
                    </Dragger>
                  </div>
                ) : (
                  <>
                    {/* Progress bar during import */}
                    {importProgress && (
                      <div style={{ marginBottom: 16 }}>
                        <Progress
                          percent={Math.round((importProgress.current / importProgress.total) * 100)}
                          status={importProgress.errorCount > 0 ? 'exception' : 'active'}
                          format={() => `${importProgress.current}/${importProgress.total}`}
                        />
                        <div style={{ marginTop: 8 }}>
                          <Tag color="green">{importProgress.successCount} амжилттай</Tag>
                          <Tag color="red">{importProgress.errorCount} алдаатай</Tag>
                        </div>
                      </div>
                    )}

                    {/* Error rows alert */}
                    {errorCount > 0 && (
                      <Alert
                        message={`${errorCount} мөрт алдаа гарлаа`}
                        description={
                          <div style={{ maxHeight: 200, overflow: 'auto' }}>
                            {importedData
                              .filter((r) => r.import_status === 'error')
                              .map((r) => (
                                <div key={r.key} style={{ marginBottom: 4 }}>
                                  <strong>{r.last_name} {r.first_name}</strong>: {r.import_error}
                                </div>
                              ))}
                          </div>
                        }
                        type="error"
                        showIcon
                        style={{ marginBottom: 16 }}
                        closable
                      />
                    )}

                    {/* Validation errors alert */}
                    {invalidCount > 0 && (
                      <Alert
                        message={`${invalidCount} мөрт алдаа байна`}
                        description="Алдаатай талбаруудыг засаад дахин оролдоно уу."
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                        action={
                          <Button size="small" type="text" onClick={() => setImportedData([])}>
                            Шинэ файл
                          </Button>
                        }
                      />
                    )}

                    {/* Success message */}
                    {invalidCount === 0 && errorCount === 0 && pendingCount > 0 && (
                      <Alert
                        message={`${importedData.length} мөр уншигдлаа`}
                        description="Бүх өгөгдөл зөв байна. Хадгалах товчийг дарж хадгална уу."
                        type="success"
                        showIcon
                        style={{ marginBottom: 16 }}
                        action={
                          <Button size="small" type="text" onClick={() => setImportedData([])}>
                            Шинэ файл
                          </Button>
                        }
                      />
                    )}

                    {/* All done message */}
                    {pendingCount === 0 && successCount > 0 && (
                      <Alert
                        message="Импорт дууслаа"
                        description={`${successCount} харваач амжилттай бүртгэгдлээ.${errorCount > 0 ? ` ${errorCount} алдаатай.` : ''}`}
                        type={errorCount === 0 ? 'success' : 'warning'}
                        showIcon
                        style={{ marginBottom: 16 }}
                      />
                    )}

                    <Table
                      dataSource={importedData}
                      columns={columns}
                      scroll={{ x: 'max-content' }}
                      pagination={{
                        pageSize: 50,
                        showSizeChanger: true,
                        showTotal: (total) => `Нийт ${total} мөр`,
                        pageSizeOptions: ['20', '50', '100', '200'],
                      }}
                      size="small"
                      loading={fileLoading}
                      rowClassName={(record) => {
                        if (record.import_status === 'success') return 'import-row-success';
                        if (record.import_status === 'error') return 'import-row-error';
                        return '';
                      }}
                    />
                  </>
                )}
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ArcherImport;
